import { attach, createEffect, createEvent, createStore } from "effector";
import { v4 as uuidv4 } from "uuid";

import {
  type ServerAnswer,
  build,
  claimResourcesApi,
  exchange,
  getMe,
  move,
  newTokenValleyPlayer,
  taskApi,
} from "../api";
import { setTutorialStep } from "../dev2dev/tutorial";
import type {
  PlayerProfile,
  Resources,
  Stock,
  TokenValleyBlockType,
} from "../types/tokenValley";
// import { balanceChangedAnalytics } from "../dev2dev/currentBalance";
// import { levelUp } from "../dev2dev/level";
// import { setTutorialStep } from "../dev2dev/tutorial";
import { $playerProfile } from "./playerProfile";

export const resourceEarned = createEvent<{
  stock2add: Stock;
  block2add: TokenValleyBlockType;
}>();

export const $playerData = createStore<PlayerProfile>(newTokenValleyPlayer);

$playerData.watch((state, _) => setTutorialStep(state.tutorial_step));

export const $updatedPrevious = createStore<number>(0);

export const getAmountOfBuildings = (type: string) =>
  $playerData
    .getState()
    .player_blocks_available.filter((availableBlock) => availableBlock === type)
    .length;

// export const addHardCurrency = createEvent<number>();

// Sync backend tutorial part and Analytics tutorial part
// $playerData.watch((playerData, _) => {
//   if (playerData.tutorial_step == -2) return;
//   setTutorialStep(playerData.tutorial_step);
// });

// export const buildCityBlock = createEffect(
//   async (props: {
//     x: number;
//     y: number;
//     block_type: string;
//   }): Promise<TapCityPlayer> => {
//     return await build({
//       x: props.x,
//       y: props.y,
//       block_type: props.block_type,
//       updated: $playerData.getState().updated,
//     });
//   },
// );

export const setTask = createEffect(
  async (props: {
    valley_block_id: number;
    cycles_planned: number;
  }): Promise<ServerAnswer> => {
    return await taskApi(attachUpdatedTime(props));
  },
)

export const moveValleyBlock = createEffect(
  async (props: {
    original_position: number;
    new_position: number;
  }): Promise<ServerAnswer> => {
    const response = await move({
      original_position: props.original_position,
      new_position: props.new_position,
      updated: $playerData.getState().updated,
    });
    return response;
  },
);
export const attachUpdatedTime = (props: any) => {
  return Object.assign(props, { updated: $playerData.getState().updated });
};
export const exchangeResources = createEffect(
  async (props: {
    exchange_position: number;
    resource_type: string;
    resource_amount: number;
    resource_price: number;
  }): Promise<ServerAnswer> => {
    const response = await exchange(attachUpdatedTime(props));
    return response;
  },
);

export const buildValleyBlock = createEffect(
  async (props: {
    position: number;
    block_type: string;
  }): Promise<ServerAnswer> => {
    const response = await build(attachUpdatedTime(props));
    return response;
  },
);

export const fetchGetPlayerData = attach({
  source: [$playerProfile],
  effect: async ([_]) => {
    return await getMe();
  },
});

export const claimResources = createEffect(
  async (props: { resource_name: Resources }): Promise<ServerAnswer> => {
    const response = await claimResourcesApi(attachUpdatedTime(props));
    return response;
  },
);

$updatedPrevious.on(
  fetchGetPlayerData.doneData,
  (_, newData) => newData.updated_previous,
);

const addResource = (
  stock: Stock | null,
  block_to: TokenValleyBlockType | null | undefined,
) => {
  if (!stock || !block_to) return;

  block_to.resource_stock = sumStock(block_to.resource_stock, stock);
};

export const sumStock = (stockOne: Stock, stockTwo: Stock): Stock => {
  return Object.fromEntries(
    [...new Set([...Object.keys(stockOne), ...Object.keys(stockTwo)])].map(
      (key) => [
        key,
        (stockOne[key as Resources] || 0) + (stockTwo[key as Resources] || 0),
      ],
    ),
  );
};

$playerData
  .on(fetchGetPlayerData.doneData, (_, newData) => newData.player)
  .on(moveValleyBlock.doneData, (_, newData) => newData.player)
  .on(buildValleyBlock.doneData, (_, newData) => newData.player)
  .on(exchangeResources.doneData, (_, newData) => newData.player)
  .on(claimResources.doneData, (_, newData) => newData.player)
  .on(setTask.doneData, (_, newData) => newData.player)
  .on(resourceEarned, (currentData, params) => {
    addResource(
      params.stock2add,
      currentData.valley_map[params.block2add.block_position],
    );
    return currentData;
  });

// export function parseCityDataIntoMap(
//   dataFromTheServer: Array<TokenValleyBlockType>,
// ): Map<string, TokenValleyBlockType> {
//   const formattedDataMap: Map<string, TokenValleyBlockType> = new Map();
//   dataFromTheServer.forEach((dataBlock) => {
//     formattedDataMap.set(dataBlock.x + "," + dataBlock.y, dataBlock);
//   });
//   return formattedDataMap;
// }

// export function parseValleyMap(
//   valleyMapData: Map<string, TokenValleyBlockType>,
//   width: number = 5,
//   height: number = 7,
// ): Array<TokenValleyBlockType> {
//   const grid: Array<TokenValleyBlockType> = [];
//   for (let x = 1; x <= width; x++) {
//     for (let y = 1; y <= height; y++) {
//       const elem = valleyMapData.get(x + "," + y);
//       if (elem) {
//         grid.push(elem);
//       } else {
//         grid.push({
//           block_position: y * 5 + x,
//           block_type: "build/void",
//           block_uuid: uuidv4(),
//           is_active: false,
//           last_synchronization_time: 0,
//           resource_stock: {},
//           block_production: []
//         });
//       }
//     }
//   }

//   return grid;
// }

export const $playerValleyMap = createStore<Array<TokenValleyBlockType>>([]).on(
  $playerData.updates,
  (_, newData) => {
    const newValley = new Array<TokenValleyBlockType>();
    for (let i = 0; i <= 46; i++) {
      if (newData.valley_map[i]) {
        newValley[i] = newData.valley_map[i];
      } else {
        newValley[i] = {
          block_position: i,
          block_level: 1,
          block_type: "build/void",
          block_uuid: uuidv4(),
          is_active: false,
          block_time_ready: 0,
          last_synchronization_time: 0,
          resource_stock: {},
          block_production: [],
        };
      }
    }
    // console.log("newValley " + JSON.stringify(newValley))
    return newValley;
  },
);
